import React, { useEffect, useState } from 'react';
import { Card, Row, Col, TimePicker, Switch, Typography, Button, Space, Tag, message, Tooltip, Divider } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import apiService from '@/services/apiService';

type TimeSlot = { time: string; enabled: boolean };

const ShopifyJobAndStatus: React.FC = () => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([{ time: '19:00', enabled: true }]);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(()=>{
    (async()=>{
      try{
        const data:any = await apiService.get('/schedules');
        const rec = (data?.data||[]).find((r:any)=> r.platform==='Shopify');
        if(rec){
          setEnabled(!!rec.enabled);
          const t = (rec.daily_time || '19:00').slice(0,5);
          const slotsFromApi = Array.isArray(rec.timeSlots) ? rec.timeSlots : [];
          let normalized: TimeSlot[] = [];
          if(slotsFromApi.length){
            if(typeof slotsFromApi[0] === 'string'){
              normalized = (slotsFromApi as string[]).map(s=> ({ time: String(s).slice(0,5), enabled: true }));
            }else{
              normalized = (slotsFromApi as any[]).map(o=> ({ time: String((o&&o.time)!=null? o.time : o).slice(0,5), enabled: 'enabled' in (o||{}) ? !!o.enabled : true }));
            }
          }
          if(!normalized.length){
            normalized = [{ time: t, enabled: true }];
          }
          setTimeSlots(normalized);
        }
      }catch{}
    })();
  },[]);

  const save = async()=>{
    try{ setSaving(true);
      const cleaned = (timeSlots || []).map(s => ({ time: /^\d{2}:\d{2}$/.test(String(s.time).trim()) ? String(s.time).trim() : '19:00', enabled: !!s.enabled }));
      const enabledTimes = cleaned.filter(s=> s.enabled).map(s=> s.time).slice(0, 6);
      if (cleaned.length > 6) {
        message.warning('最多可新增 6 個時段，已自動截取前 6 個');
      }
      await apiService.put(`/schedules/${encodeURIComponent('Shopify')}`, { enabled: !!enabled, dailyTime: (enabledTimes[0] || '19:00'), timeSlots: enabledTimes });
      message.success('已保存');
    }catch{ message.error('保存失敗'); }
    finally{ setSaving(false); }
  };

  const runNow = async()=>{
    try{ setSaving(true);
      await apiService.post(`/schedules/${encodeURIComponent('Shopify')}/run`, {});
      message.success('已觸發手動拉單');
    }catch{ message.error('觸發失敗'); }
    finally{ setSaving(false); }
  };

  return (
    <Card>
      <Row align="middle" justify="space-between">
        <Col>
          <Typography.Title level={3} style={{ marginBottom: 8 }}>連線狀態</Typography.Title>
          <Typography.Title level={2} style={{ marginTop: 0 }}>{enabled ? 'Enabled' : 'Disabled'}</Typography.Title>
          <div style={{ marginTop: 8 }}>{enabled ? <Tag color="green">啟用</Tag> : <Tag>停用</Tag>}</div>
        </Col>
        <Col>
          <Space>
            <Button type="primary" onClick={save} loading={saving}>保存</Button>
            <Button onClick={runNow} loading={saving}>手動拉單</Button>
          </Space>
        </Col>
      </Row>

      <Divider style={{ margin: '16px 0' }} />

      <Row gutter={[16, 16]} align="middle">
        <Col span={24}>
          <Typography.Text strong>連接/斷開</Typography.Text>
        </Col>
        <Col span={24}>
          <Tooltip title="切換為『連接』後，系統會依下方『已啟用』時段自動拉取 Shopify 訂單；切換為『斷開』時將不會自動拉單（仍可手動拉單）。">
            <Switch checked={enabled} onChange={setEnabled} checkedChildren="連接" unCheckedChildren="斷開" />
          </Tooltip>
        </Col>
      </Row>

      <Divider style={{ margin: '16px 0' }} />

      <Row align="middle" justify="space-between" style={{ marginBottom: 8 }}>
        <Col>
          <Typography.Text strong>時段列表</Typography.Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<PlusOutlined />} onClick={()=> setTimeSlots(prev=> {
              if ((prev?.length || 0) >= 6) { message.warning('最多可新增 6 個時段'); return prev; }
              return [...prev, { time: '19:00', enabled: true }];
            })}>新增時段</Button>
            <Button onClick={()=> setTimeSlots(prev=> prev.map(s=> ({ ...s, enabled: true })))}>一鍵啟用全部</Button>
            <Button onClick={()=> setTimeSlots(prev=> prev.map(s=> ({ ...s, enabled: false })))}>一鍵停用全部</Button>
          </Space>
        </Col>
      </Row>

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {timeSlots.map((slot,idx)=> (
          <Row key={idx} gutter={[12, 12]} align="middle">
            <Col>
              <Switch checked={!!slot.enabled} onChange={(v)=> setTimeSlots(prev=> prev.map((x,i)=> i===idx ? ({ ...x, enabled: !!v }) : x))} checkedChildren="啟用" unCheckedChildren="停用" />
            </Col>
            <Col>
              <TimePicker value={slot.time ? (dayjs(slot.time,'HH:mm') as unknown as Dayjs) : null as unknown as Dayjs} format="HH:mm" minuteStep={5}
                onChange={(t)=> setTimeSlots(prev=> t ? prev.map((x,i)=> i===idx ? ({ ...x, time: t.format('HH:mm') }) : x) : prev.filter((_,i)=> i!==idx))} />
            </Col>
            <Col>
              <Button type="text" danger icon={<DeleteOutlined />} onClick={()=> setTimeSlots(prev=> prev.filter((_,i)=> i!==idx))}>刪除</Button>
            </Col>
          </Row>
        ))}
      </Space>

      <Typography.Paragraph type="secondary" style={{ marginTop: 16 }}>
        啟用後，系統會在每日所設時間（HKT）自動拉取 Shopify 訂單；亦可點擊「手動拉單」立即執行一次。
      </Typography.Paragraph>
    </Card>
  );
};

export default ShopifyJobAndStatus;


